// hooks/useCollectionQueries.ts
import { COLLECTION_SLUGS, FLAGS, LIMITS } from '@/lib/constants';
import { logger } from '@/lib/logger';
import { CATALOG_STALE_TIME, queryKeys, USER_DATA_STALE_TIME } from '@/lib/queryClient';
import {
  getCollectionBySlugs,
  type CollectionInfo
} from '@/supabase/services/collections';
import {
  fetchLessonsByCategories,
  type SortOrder
} from '@/supabase/services/lesson';
import {
  fetchContinueLessonsForDashboard,
  fetchCourseThumbnailsCategories,
  fetchLatestLessons,
  fetchLinkedCourses,
} from '@/supabase/services/optimizedCourse';
import { getUserLessonsForProgress } from '@/supabase/services/userLessons';
import type { Lesson, UserLesson } from '@/types';
import { extractSimpleCourseIds } from '@/utils/courseHelpers';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

// Extend query keys for collections
const collectionQueryKeys = {
  collection: (courseSlug: string, collectionSlug: string) => 
    ['collection', courseSlug, collectionSlug] as const,
  lessons: (collectionId: number, sortOrder: SortOrder, minStatus: number) => 
    ['collection', collectionId, 'lessons', sortOrder, minStatus] as const,
  userLessons: (userId: string | null, lessonIds: number[]) => 
    ['collection', 'userLessons', userId, lessonIds] as const,
};

// Hook to fetch collection metadata by slugs
// Uses 30min cache in production/staging, 1min in dev

export function useCollection(
  courseSlug: string,
  collectionSlug: string,
  enabled: boolean = true
): UseQueryResult<CollectionInfo, Error> {
  return useQuery({
    queryKey: collectionQueryKeys.collection(courseSlug, collectionSlug),
    queryFn: async () => {
      logger.debug('[Cache] Fetching collection:', { courseSlug, collectionSlug });
      return getCollectionBySlugs(courseSlug, collectionSlug);
    },
    staleTime: CATALOG_STALE_TIME,
    enabled: enabled && !!courseSlug && !!collectionSlug,
  });
}

// Hook to fetch collection lessons
//  Uses 30min cache in production/staging, 1min in dev
// Cache key includes sortOrder to have separate cache per sort
export function useCollectionLessons(
  collectionId: number | undefined,
  sortOrder: SortOrder,
  minStatus: number,
  enabled: boolean = true
): UseQueryResult<Lesson[], Error> {
  return useQuery({
    queryKey: collectionQueryKeys.lessons(collectionId!, sortOrder, minStatus),
    queryFn: async () => {
      logger.debug('[Cache] Fetching collection lessons:', { collectionId, sortOrder });
      return fetchLessonsByCategories([collectionId!], sortOrder, minStatus);
    },
    staleTime: CATALOG_STALE_TIME,
    enabled: enabled && !!collectionId,
  });
}

// Hook to fetch user progress for collection lessons
// No cache (user data that changes frequently)

export function useUserLessonsProgress(
  userId: string | null | undefined,
  lessonIds: number[]
): UseQueryResult<Record<number, UserLesson>, Error> {
  return useQuery({
    queryKey: collectionQueryKeys.userLessons(userId || null, lessonIds),
    queryFn: async () => {
      if (!userId || lessonIds.length === 0) {
        return {};
      }
      logger.debug('[Cache] Fetching user lessons progress (no cache):', { userId, count: lessonIds.length });
      return getUserLessonsForProgress(userId, lessonIds);
    },
    staleTime: USER_DATA_STALE_TIME, // 0 - no cache for user progress
    enabled: !!userId && lessonIds.length > 0,
    gcTime: 1 * 60 * 1000, // 1 min garbage collection
  });
}

export function useCollectionPageData(
  courseSlug: string,
  collectionSlug: string,
  sortOrder: SortOrder,
  minStatus: number,
  userId: string | null | undefined,
  enabled: boolean = true
) {

  const collectionQuery = useCollection(courseSlug, collectionSlug, enabled);
  const collection = collectionQuery.data;
  
  // Handle special cases: "latest" and "continue"
  const isLatest = collectionSlug === COLLECTION_SLUGS.LATEST;
  const isContinue = collectionSlug === COLLECTION_SLUGS.CONTINUE;


  // - If collection.sort_orders_enabled === false  -> always use 'by_position'
  // - Otherwise, use the persisted sortOrder from state
  const sortOrdersEnabled = collection?.sort_orders_enabled ?? true;
  const effectiveSortOrder: SortOrder =
    !sortOrdersEnabled ? 'by_position' : sortOrder;
  
  // For "latest": fetch latest lessons directly
  const latestLessonsQuery = useQuery({
    queryKey: ['collection', 'latest', collection?.courseId, minStatus],
    queryFn: async () => {
      if (!collection?.courseId) return [];
      return fetchLatestLessons(collection.courseId, LIMITS.COLLECTION_SEE_ALL, minStatus);
    },
    staleTime: 30 * 60 * 1000,
    enabled: enabled && isLatest && !!collection?.courseId,
  });
  
  // For "continue": fetch course thumbnails to get simple course ids, then fetch all continue lessons (same as dashboard)
  const courseThumbnailsQuery = useQuery({
    queryKey: [...queryKeys.course.courseThumbnailsCategories(collection?.courseId ?? 0), minStatus],
    queryFn: () => fetchCourseThumbnailsCategories(collection!.courseId, minStatus),
    staleTime: CATALOG_STALE_TIME,
    enabled: enabled && isContinue && !!collection?.courseId,
  });

  const simpleCourseIds = useMemo(
    () => extractSimpleCourseIds(courseThumbnailsQuery.data),
    [courseThumbnailsQuery.data]
  );

  const linkedCoursesQuery = useQuery({
    queryKey: ['collection', 'continue', collection?.courseId, 'linkedCourses', minStatus],
    queryFn: () => fetchLinkedCourses(collection!.courseId, minStatus),
    staleTime: CATALOG_STALE_TIME,
    enabled: enabled && isContinue && !!collection?.courseId,
  });

  const linkedCourseIds = useMemo(
    () => linkedCoursesQuery.data?.courseIds ?? [],
    [linkedCoursesQuery.data?.courseIds]
  );

  const continueLessonsQuery = useQuery({
    queryKey: ['collection', 'continue', userId, collection?.courseId, simpleCourseIds, linkedCourseIds],
    queryFn: () =>
      fetchContinueLessonsForDashboard(
        userId!,
        collection!.courseId,
        simpleCourseIds,
        minStatus,
        linkedCourseIds,
        false,
        FLAGS.CONTINUE_INCLUDE_ALL_COURSES
      ),
    staleTime: USER_DATA_STALE_TIME,
    enabled:
      enabled &&
      isContinue &&
      !!userId &&
      !!collection?.courseId,
  });
  
  // For regular collections/sections: fetch by category
const lessonsEnabled =
    !!collection?.id &&
    !isLatest &&
    !isContinue &&
    (sortOrdersEnabled ? enabled : true);

  const lessonsQuery = useCollectionLessons(
    collection?.id, 
    effectiveSortOrder, 
    minStatus, 
    lessonsEnabled
  );
  
  // Combine lessons based on type
  const lessons = useMemo(() => {
    if (isLatest) return latestLessonsQuery.data || [];
    if (isContinue) return continueLessonsQuery.data || [];
    return lessonsQuery.data || [];
  }, [isLatest, isContinue, latestLessonsQuery.data, continueLessonsQuery.data, lessonsQuery.data]);
  
  // Sort lessonIds to ensure consistent queryKey regardless of sort order
  const lessonIds = useMemo(() => {
    const ids = lessons.map(l => l.id);
    return [...ids].sort((a, b) => a - b);
  }, [lessons]);
  
  const userProgressQuery = useUserLessonsProgress(userId, lessonIds);
  const userLessonsMap = userProgressQuery.data || {};
  
  const isLoading = collectionQuery.isLoading || 
    (isLatest ? latestLessonsQuery.isLoading : 
     isContinue ? continueLessonsQuery.isLoading : 
     lessonsQuery.isLoading) || 
    userProgressQuery.isLoading;
    
  const isError = collectionQuery.isError || 
    (isLatest ? latestLessonsQuery.isError : 
     isContinue ? continueLessonsQuery.isError : 
     lessonsQuery.isError) || 
    userProgressQuery.isError;
    
  const error = collectionQuery.error || 
    (isLatest ? latestLessonsQuery.error : 
     isContinue ? continueLessonsQuery.error : 
     lessonsQuery.error) || 
    userProgressQuery.error;
  
  
  if (!isLoading && !isError) {
    logger.debug('[Cache] Collection page loaded:', {
      collection: collection?.slug,
      sortOrder,
      isLatest,
      isContinue,
    });
  }
  
  return {
    collection,
    lessons,
    userLessonsMap,
    isLoading,
    isError,
    error,
    queries: {
      collectionQuery,
      lessonsQuery: isLatest ? latestLessonsQuery : isContinue ? continueLessonsQuery : lessonsQuery,
      userProgressQuery,
    },
  };
}

